/* eslint-disable @typescript-eslint/no-explicit-any */
// tslint:disable max-file-line-count
import {
  concat,
  filter,
  find,
  forEach,
  includes,
  join,
  last,
  map,
  range,
  size,
  some,
  sortBy,
  toString,
} from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import * as Service from 'types/service';
import { Context, core, safeEnumCompare } from '@tock/coretypes';
import { ElaboratedTicketGroup } from 'types';
import {
  EventAction,
  EventAttributesActionLocation,
  EventCustomAttributes,
} from 'utils/analytics/amplitudeEventConstants';
import { TicketGroupsByDateTime } from 'selectors/ticketGroupsByDateTimeSelector';
import { assertNever, filterNilValues } from '@tock/shared/utils/typeUtils';
import { getBookableIntervalsForFlexibleTicketGroup } from 'utils/flexibleTimeUtils';
import { getFullName } from '@tock/shared/utils/patron';
import { isDisputed } from 'utils/disputeUtils';
import { isImported } from 'utils/purchaseUtils';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import { isUnassignedVisit } from 'utils/reservationDetailsUtils';
import CalendarItemActionNames from 'constants/CalendarItemActionNames';
import TableTypeConstants from 'constants/TableTypeConstants';
import TicketGroupStateConstants from 'constants/TicketGroupStateConstants';
import protos from 'utils/protos';
import ticketGroupHelpers from 'utils/ticketGroupHelpers';

const { PurchaseVerifyInfo } = protos;
const InitiatedFrom = PurchaseVerifyInfo.InitiatedFrom;

/* Internal Utilities */
const calculateDescriptionTextForUnassignedItem = (
  minPurchaseSize: number,
  maxPurchaseSize: number
) => {
  let descriptionText = '';
  if (minPurchaseSize === maxPurchaseSize) {
    descriptionText = String(maxPurchaseSize);
  } else {
    descriptionText = `${minPurchaseSize}-${maxPurchaseSize}`;
  }
  return descriptionText;
};
/* End Internal Utilities */

export interface CalendarItemAction {
  disabled?: boolean;
  key: CalendarItemActionNames;
  isConfirm: boolean;
  showInModify?: boolean;
  isDestructive?: boolean;
  showOnMobile?: boolean;
  text: string;
  calendarItemState: CalendarItemState;
  showUpdatedBadge?: boolean;
  variant?: 'primary' | 'subtle';
  amplitude?: AmplitudeTrackingValues;
}

export interface AmplitudeTrackingValues {
  event: `${EventAction}`;
  data?: EventCustomAttributes;
}

export type CalendarItemState =
  'AVAILABLE' | 'HELD' | 'LOCKED' | 'COMMUNAL' | 'SOLD' | 'TIME_FLEXIBLE';

export enum CalendarItemStateEnum {
  AVAILABLE = 'AVAILABLE',
  HELD = 'HELD',
  LOCKED = 'LOCKED',
  COMMUNAL = 'COMMUNAL',
  SOLD = 'SOLD',
  TIME_FLEXIBLE = 'TIME_FLEXIBLE',
}

interface SharedContent {
  id: string;
  finishedByTime?: string;
  ticketGroup: core.TicketGroup;
  peerTicketGroupIsLocked: boolean;
  isBookable: boolean;
  shortCode?: string;
  ticketCounts?: TicketCounts;
}

export interface GetActionsOptions {
  isReservationOpen?: boolean;
  hideBookMultiple?: boolean;
  isHost: boolean;
}

export class CalendarItem {
  id: string;
  annotationText: string;
  // TODO [Mcc 07/10/19] dateTime required
  dateTime?: string;
  descriptionText: string;
  finishedByTime?: string;
  forfeited: number;
  isBookable: boolean;
  numBookable: number;
  purchase: core.PastPurchase;
  ticketSubset: core.TicketSubset;
  ticketGroup: core.TicketGroup;
  ticketCounts?: TicketCounts;
  state: CalendarItemState;
  isBlocked: boolean;

  constructor(
    id: string,
    annotationText: string,
    descriptionText: string,
    forfeited: number,
    isBookable: boolean,
    numBookable: number,
    purchase: core.PastPurchase,
    ticketSubset: core.TicketSubset,
    ticketGroup: core.TicketGroup,
    state: CalendarItemState,
    finishedByTime: string | undefined,
    ticketCounts: TicketCounts | undefined
  ) {
    this.id = id;
    this.annotationText = annotationText;
    this.descriptionText = descriptionText;
    this.forfeited = forfeited;
    this.isBookable = isBookable;
    this.numBookable = numBookable;
    this.purchase = purchase;
    this.ticketSubset = ticketSubset;
    this.ticketGroup = ticketGroup;
    this.state = state;
    this.finishedByTime = finishedByTime;
    this.ticketCounts = ticketCounts;
  }

  getIsFlex() {
    return !!this.finishedByTime;
  }

  getActions(context: Context, options?: GetActionsOptions): CalendarItemAction[] {
    return [];
  }

  getAnnotation() {
    return '';
  }

  getStatus() {
    return '';
  }

  getDescriptionString(): string {
    return '';
  }

  getShortCode() {
    return oc(this.ticketGroup).ticketType[0].shortCode();
  }

  getSquareColor(): string {
    return '';
  }

  getGuestName() {
    return '';
  }

  getExperienceName() {
    return '';
  }

  getPartySize() {
    return oc(this.ticketGroup).maxPurchaseSize();
  }
}

class AvailableOrHeld extends CalendarItem {
  allowedPurchaseSizes: number[];
  minPurchaseSize: number;
  maxPurchaseSize: number;
  maxSize: number;
  constructor(
    sharedContent: SharedContent,
    numBookable: number,
    allowedPurchaseSizes: number[],
    maxSize: number,
    ticketSubset: core.TicketSubset
  ) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      (sharedContent as any).numBookable,
      (sharedContent as any).purchase,
      (sharedContent as any).ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );
    const sortedAllowedPurchaseSizes = sortBy(allowedPurchaseSizes);
    const minPurchaseSize = sortedAllowedPurchaseSizes[0];
    const maxPurchaseSize = last(allowedPurchaseSizes) as number;

    this.descriptionText = calculateDescriptionTextForUnassignedItem(
      minPurchaseSize,
      maxPurchaseSize
    );
    this.allowedPurchaseSizes = sortedAllowedPurchaseSizes;
    this.minPurchaseSize = minPurchaseSize;
    this.maxPurchaseSize = maxPurchaseSize;
    this.ticketSubset = ticketSubset;
    this.maxSize = maxSize;
    this.numBookable = numBookable;
  }

  getDescriptionString() {
    return this.descriptionText;
  }
}

class Available extends AvailableOrHeld {
  constructor(
    sharedContent: SharedContent,
    numBookable: number,
    allowedPurchaseSizes: number[],
    maxSize: number,
    annotationText: string
  ) {
    super(sharedContent, numBookable, allowedPurchaseSizes, maxSize, undefined as any);
    this.annotationText = annotationText;
    this.state = TicketGroupStateConstants.AVAILABLE;
    this.dateTime = oc(sharedContent.ticketGroup).dateTime();
  }

  getSquareColor() {
    return 'SquareColor--available';
  }

  getExperienceName() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.ticketGroup.ticketType[0].name!;
  }

  getPartySize() {
    return this.ticketGroup.maxPurchaseSize;
  }

  getStatus() {
    const status: string[] = [];
    if (this.ticketGroup.unlisted) {
      status.push('Hidden');
    }
    return join(status, ', ');
  }

  getAnnotation() {
    return this.annotationText;
  }

  getActions(context: Context, options?: GetActionsOptions): CalendarItemAction[] {
    const isReservationOpen = !!(options && options.isReservationOpen);

    const actions: CalendarItemAction[] = [
      {
        key: CalendarItemActionNames.SELECT,
        disabled: true,
        isConfirm: false,
        text: 'View details',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'View details',
          },
        },
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.MODIFY,
        disabled: false,
        isConfirm: false,
        text: 'Modify',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Modify',
          },
        },
        calendarItemState: this.state,
      },
      {
        disabled: !this.isBookable || isReservationOpen,
        key: CalendarItemActionNames.SELL,
        isConfirm: false,
        showInModify: true,
        text: 'Book reservation',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Book now',
          },
        },
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.SEND_TO_WAITLIST,
        disabled: false,
        isConfirm: false,
        text: 'Alert Notify list',
        showInModify: true,
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Alert Notify list',
            actionLocation: EventAttributesActionLocation.TIMELINE_BODY,
          },
        },
      },
      {
        key: CalendarItemActionNames.MOVE,
        disabled: context.isFlexBusiness() || options?.isHost,
        isConfirm: false,
        text: 'Move',
        showInModify: true,
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.EDIT,
        disabled: false,
        isConfirm: false,
        text: 'Edit',
        showInModify: true,
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.TOGGLE,
        disabled: !this.isBookable,
        isConfirm: false,
        text: 'Hold',
        showInModify: true,
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Hold',
          },
        },
      },
      {
        key: CalendarItemActionNames.HOLD_FOR,
        disabled: !this.isBookable,
        isConfirm: false,
        text: 'Hold',
        showInModify: true,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Hold',
          },
        },
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.DELETE,
        disabled: false,
        isConfirm: true,
        isDestructive: true,
        text: 'Remove',
        showInModify: true,
        calendarItemState: this.state,
      },
    ];

    const keysFilteredForCommunalTable = [CalendarItemActionNames.HOLD_FOR];

    const keysFilteredForRegularTable = [CalendarItemActionNames.TOGGLE];

    if (ticketGroupHelpers.isCommunal(this.ticketGroup)) {
      return filter(actions, (action) => {
        return !includes(keysFilteredForCommunalTable, action.key);
      });
    } else {
      return filter(actions, (action) => {
        return !includes(keysFilteredForRegularTable, action.key);
      });
    }
  }
}

class Held extends AvailableOrHeld {
  constructor(
    sharedContent: SharedContent,
    numBookable: number,
    allowedPurchaseSizes: number[],
    maxSize: number,
    ticketSubset: core.TicketSubset
  ) {
    super(sharedContent, numBookable, allowedPurchaseSizes, maxSize, ticketSubset);
    this.state = TicketGroupStateConstants.HELD;
    this.dateTime = ticketSubset.requestedDateTime || oc(sharedContent.ticketGroup).dateTime();
  }

  getSquareColor() {
    return 'SquareColor--held';
  }

  getStatus() {
    const status: string[] = [];
    if (this.ticketSubset && this.ticketSubset.heldForPatron) {
      status.push('Held for a guest');
    }
    if (this.ticketGroup.unlisted) {
      status.push('Hidden');
    }
    return join(status, ', ');
  }

  getActions(context: Context, options?: GetActionsOptions): CalendarItemAction[] {
    const isReservationOpen = !!(options && options.isReservationOpen);
    let actions: CalendarItemAction[] = [];
    actions = [
      {
        disabled: ticketGroupHelpers.isCommunal(this.ticketGroup),
        isConfirm: false,
        key: CalendarItemActionNames.SELECT,
        text: 'View',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'View details',
          },
        },
      },
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.MODIFY,
        text: 'Modify',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Modify held',
          },
        },
      },
      {
        disabled: !this.isBookable || isReservationOpen,
        key: CalendarItemActionNames.SELL,
        isConfirm: false,
        showInModify: true,
        text: 'Book reservation',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Book now',
          },
        },
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.SEND_TO_WAITLIST,
        disabled: false,
        isConfirm: false,
        text: 'Alert Notify list',
        showInModify: true,
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Alert Notify list',
            actionLocation: EventAttributesActionLocation.TIMELINE_BODY,
          },
        },
      },
      {
        key: CalendarItemActionNames.MOVE,
        disabled: context.isFlexBusiness() || options?.isHost,
        isConfirm: false,
        text: 'Move',
        showInModify: true,
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Move held',
          },
        },
      },
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.EDIT,
        showInModify: true,
        text: 'Edit',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Edit held',
          },
        },
      },
      {
        disabled: !this.isBookable,
        isConfirm: false,
        key: CalendarItemActionNames.TOGGLE,
        showInModify: true,
        text: ticketGroupHelpers.isCommunal(this.ticketGroup) ? 'Hold/unhold' : 'Remove Hold',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: ticketGroupHelpers.isCommunal(this.ticketGroup)
              ? 'Hold/unhold held'
              : 'Remove held',
          },
        },
      },
      {
        disabled: !this.isBookable,
        isConfirm: false,
        key: CalendarItemActionNames.HOLD_FOR,
        showInModify: true,
        text: 'Hold for guest',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Hold for',
          },
        },
        calendarItemState: this.state,
      },
      {
        disabled: false,
        isConfirm: true,
        isDestructive: true,
        key: CalendarItemActionNames.DELETE,
        showInModify: true,
        text: 'Remove',
        calendarItemState: this.state,
      },
    ];

    if (this.ticketSubset && this.ticketSubset.heldForPatron) {
      const action = find(actions, { key: CalendarItemActionNames.HOLD_FOR });
      if (action) {
        action.key = CalendarItemActionNames.CANCEL_HOLD_FOR;
        action.text = 'Remove hold for guest';
      }
    }

    const keysFilteredForCommunalTable = [
      CalendarItemActionNames.HOLD_FOR,
      CalendarItemActionNames.CANCEL_HOLD_FOR,
    ];

    if (ticketGroupHelpers.isCommunal(this.ticketGroup)) {
      return filter(actions, (action) => {
        return !includes(keysFilteredForCommunalTable, action.key);
      });
    }

    return actions;
  }
}

class Locked extends CalendarItem {
  emailSent: boolean;
  purchaseVerifyInfo: core.PurchaseVerifyInfo;
  constructor(
    sharedContent: SharedContent,
    purchaseVerifyInfo: any,
    ticketSubset: core.TicketSubset
  ) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      0,
      (sharedContent as any).purchase,
      ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );

    this.dateTime = ticketSubset.requestedDateTime || oc(sharedContent.ticketGroup).dateTime();
    this.emailSent = !!purchaseVerifyInfo;
    this.purchaseVerifyInfo = purchaseVerifyInfo;
    this.state = TicketGroupStateConstants.LOCKED;
  }

  getDescriptionString() {
    return toString(this.ticketSubset.ticketCount);
  }

  getSquareColor() {
    return 'SquareColor--locked';
  }

  getShortCode() {
    return this.ticketSubset.purchaseVerifyInfo
      ? this.ticketSubset.purchaseVerifyInfo.ticketTypeShortCode
      : super.getShortCode();
  }
  getGuestName() {
    return getFullName(oc(this.purchaseVerifyInfo).patron());
  }

  getExperienceName() {
    return (
      oc(this.purchaseVerifyInfo).ticketTypeName() ||
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.ticketGroup.ticketType[0].name!
    );
  }

  getPartySize() {
    return oc(this.purchaseVerifyInfo).ticketCount() || this.ticketSubset.ticketCount;
  }
  getStatus(): string {
    const typeEnum = this.ticketSubset.purchaseVerifyInfo?.type ?? 'EMAIL';
    const type = typeEnum === 'EMAIL' ? 'email' : 'text';
    const status: string[] = [];
    const conciergeLabel = safeEnumCompare(
      oc(this.ticketSubset.purchaseVerifyInfo).initiatedFrom(InitiatedFrom.DASHBOARD),
      InitiatedFrom,
      InitiatedFrom.CONSUMER
    )
      ? 'concierge '
      : '';

    if (this.ticketSubset.purchaseVerifyInfo) {
      status.push(`Locked for a guest (${conciergeLabel}book via ${type})`);
    }
    return join(status, ', ');
  }

  getActions(context: Context): CalendarItemAction[] {
    const typeEnum = this.ticketSubset.purchaseVerifyInfo?.type ?? 'EMAIL';
    const type = typeEnum === 'EMAIL' ? 'email' : 'text';
    return [
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.SELECT,
        text: 'View',
        calendarItemState: this.state,
      },
      {
        disabled: !this.ticketSubset.purchaseVerifyInfo,
        isConfirm: false,
        key: CalendarItemActionNames.CANCEL_EMAIL_HANDSELL,
        text: 'Cancel reservation',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Cancel purchase',
          },
        },
        calendarItemState: this.state,
      },
      {
        disabled: !this.ticketSubset.purchaseVerifyInfo,
        isConfirm: false,
        key: CalendarItemActionNames.RESEND_EMAIL_HANDSELL,
        text: `Resend ${type} invite`,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: `Resend book by ${type}`,
          },
        },
        calendarItemState: this.state,
      },
    ];
  }
}

class Sold extends CalendarItem {
  constructor(sharedContent: SharedContent, purchase: core.PastPurchase, forfeited: number) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      (sharedContent as any).numBookable,
      (sharedContent as any).purchase,
      (sharedContent as any).ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );
    this.dateTime = purchase.ticketDateTime;
    this.purchase = purchase;
    this.forfeited = forfeited;
    this.numBookable = 0;
    this.state = TicketGroupStateConstants.SOLD;
  }

  getDescriptionString() {
    return toString(this.purchase.ticketCount);
  }

  getSquareColor() {
    return isDisputed(this.purchase) ? 'SquareColor--disputed' : 'SquareColor--reserved';
  }

  getShortCode() {
    return oc(this.purchase).ticketType.shortCode();
  }

  getGuestName() {
    return getFullName(oc(this.purchase).dinerPatron[0]());
  }

  getExperienceName() {
    return oc(this.purchase).ticketType.name('');
  }

  getPartySize() {
    return oc(this.purchase).ticketCount(0);
  }
  getStatus() {
    const status: string[] = [];

    if (isImported(this.purchase)) {
      status.push('Imported');
    }
    if (oc(this.purchase).ticketType.unlisted()) {
      status.push('Hidden');
    }
    return join(status, ', ');
  }

  getActions(context: Context, options?: GetActionsOptions): CalendarItemAction[] {
    const disputed = isDisputed(this.purchase);

    const isReservationOpen = !!(options && options.isReservationOpen);
    const isHost = !!(options && options.isHost);
    const hasPaymentCardHoldBeenCharged =
      (this.purchase.paymentCardHold?.status ?? 'HOLD_PENDING') !== 'HOLD_PENDING';
    const isUnassigned = isUnassignedVisit(this.purchase?.visit);

    const actions: CalendarItemAction[] = [
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.SELECT,
        showOnMobile: true,
        text: 'View guest',
        track: {
          category: 'Inventory',
          action: 'Inventory quick action clicked',
          label: 'View details',
        },
      },
      {
        // Turn off all edits on a purchase if it is currently in dispute or the dispute was lost.
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.MODIFY,
        showOnMobile: true,
        text: 'Modify',
      },
      {
        disabled:
          this.purchase.transferred ||
          disputed ||
          this.purchase.cancelledOrRefunded ||
          hasPaymentCardHoldBeenCharged ||
          isReservationOpen, // Can't edit if it was transferred or cancelled.
        isConfirm: false,
        key: CalendarItemActionNames.EDIT_SOLD,
        showInModify: true,
        showOnMobile: true,
        text: 'Edit reservation',
        track: {
          category: 'Inventory',
          action: 'Inventory quick action clicked',
          label: 'Edit Purchase',
        },
      },
      {
        // Can't refund if it's Braintree or Authorize
        // Or if it's been transferred
        disabled:
          this.purchase.processorType === 'BRAINTREE_LEGACY' ||
          this.purchase.processorType === 'AUTHORIZE_LEGACY' ||
          this.purchase.transferred ||
          this.purchase.amountPaidCents === 0 ||
          disputed,
        isConfirm: false,
        key: CalendarItemActionNames.COMP,
        showInModify: true,
        showOnMobile: true,
        text: 'Comp guest',
      },
      context.isFlexBusiness()
        ? {
            disabled: false,
            isConfirm: false,
            key: CalendarItemActionNames.ASSIGN_TABLE,
            showInModify: true,
            showOnMobile: false,
            text: isUnassigned ? 'Assign table(s)' : 'Assign to multiple tables',
            track: {
              category: 'Inventory',
              action: 'Inventory quick action clicked',
              label: isUnassigned ? 'Assign table(s)' : 'Assign to multiple tables',
            },
          }
        : undefined,
      {
        disabled:
          this.purchase.transferred ||
          disputed ||
          this.purchase.ticketType?.deliveryServiceProvider !== 'INTERNAL' ||
          hasPaymentCardHoldBeenCharged ||
          isReservationOpen, // Can't reschedule if it was transferred
        isConfirm: false,
        key: CalendarItemActionNames.RESCHEDULE,
        showInModify: true,
        showOnMobile: false,
        showUpdatedBadge: context.isFeatureEnabled('NEW_RESCHEDULE_FLOW'),
        text: 'Reschedule',
        track: {
          category: 'Inventory',
          action: 'Inventory quick action clicked',
          label: 'Reschedule purchase',
        },
      },
      {
        disabled:
          disputed ||
          isReservationOpen ||
          isHost ||
          isOrderVariety(this.purchase.ticketTypeVariety) ||
          hasPaymentCardHoldBeenCharged ||
          this.purchase.ticketTypeVariety === 'TABLESIDE',
        isConfirm: false,
        key: CalendarItemActionNames.TRANSFER,
        showInModify: true,
        showOnMobile: true,
        text: 'Transfer to another guest',
        track: {
          category: 'Inventory',
          action: 'Inventory quick action clicked',
          label: 'Transfer purchase',
        },
      },
      isUnassigned || !context.isFlexBusiness()
        ? (undefined as any)
        : {
            disabled: false,
            isConfirm: false,
            key: oc(this.purchase).visit.pinnedToTable()
              ? CalendarItemActionNames.UNPIN
              : CalendarItemActionNames.PIN,
            showInModify: true,
            showOnMobile: false,
            track: {
              category: 'Inventory',
              action: 'Inventory quick action clicked',
              label: oc(this.purchase).visit.pinnedToTable() ? 'Unpin' : 'Pin',
            },
            text: oc(this.purchase).visit.pinnedToTable() ? 'Unpin from table' : 'Pin to table',
          },
      {
        disabled: false,
        isConfirm: true,
        isDestructive: true,
        key: CalendarItemActionNames.CANCEL_PURCHASE,
        showInModify: true,
        showOnMobile: true,
        text: 'Cancel reservation',
        track: {
          category: 'Inventory',
          action: 'Inventory quick action clicked',
          label: 'Cancel purchase',
        },
      },
    ];

    if (this.purchase.inTransfer) {
      actions.push({
        disabled: !this.purchase.inTransfer || disputed,
        isConfirm: true,
        key: CalendarItemActionNames.TRANSFER_CANCEL,
        showInModify: true,
        showOnMobile: true,
        text: 'Cancel transfer',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Cancel transfer',
          },
        },
        calendarItemState: this.state,
      });
    }

    return filter(actions, filterNilValues);
  }
}

export class WalkIn extends CalendarItem {
  static EXPERIENCE_NAME = 'Walk-in';
  dateTime: string;
  party: Service.Party;
  constructor(sharedContent: SharedContent, party: Service.Party, dateTime: string) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      (sharedContent as any).numBookable,
      (sharedContent as any).purchase,
      (sharedContent as any).ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );
    this.party = party;
    this.dateTime = dateTime;
    this.numBookable = 0;
    this.state = TicketGroupStateConstants.SOLD;
  }

  getDescriptionString() {
    return toString(this.party.numPeoplePresent);
  }

  getSquareColor() {
    return 'SquareColor--reserved';
  }

  getShortCode() {
    return 'WALK-IN';
  }
  getGuestName() {
    return this.party.name;
  }

  getExperienceName() {
    return WalkIn.EXPERIENCE_NAME;
  }

  getPartySize() {
    return this.party.numPeoplePresent;
  }

  getStatus() {
    return '';
  }

  getActions() {
    return [];
  }
}

class Communal extends CalendarItem {
  constructor(sharedContent: SharedContent, forfeited: number) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      (sharedContent as any).numBookable,
      (sharedContent as any).purchase,
      (sharedContent as any).ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );
    this.dateTime = oc(sharedContent.ticketGroup).dateTime();
    this.forfeited = forfeited;
    this.state = 'COMMUNAL';
  }

  getDescriptionString() {
    return (
      this.ticketGroup.maxSize.toString() +
      (this.ticketGroup.allowsStragglers ? 'S' : '') +
      (this.ticketGroup.isEvensOnly ? 'E' : '')
    );
  }

  getSquareColor() {
    return 'SquareColor--communal';
  }

  getStatus() {
    return '';
  }

  getActions(context: Context): CalendarItemAction[] {
    return [
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.EDIT,
        text: 'Edit',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Edit purchase',
          },
        },
      },
      {
        disabled: false,
        isConfirm: false,
        key: CalendarItemActionNames.TOGGLE,
        text: 'Hold/unhold',
        calendarItemState: this.state,
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Hold',
          },
        },
      },
      {
        disabled: false,
        isConfirm: true,
        key: CalendarItemActionNames.DELETE,
        text: 'Remove',
        calendarItemState: this.state,
      },
    ];
  }
}

export class TimeFlexible extends CalendarItem {
  dateTime: string;

  constructor(sharedContent: SharedContent, dateTime: string, numBookable: number) {
    super(
      sharedContent.id,
      (sharedContent as any).annotationText,
      (sharedContent as any).descriptionText,
      (sharedContent as any).forfeited,
      sharedContent.isBookable,
      (sharedContent as any).numBookable,
      (sharedContent as any).purchase,
      (sharedContent as any).ticketSubset,
      sharedContent.ticketGroup,
      (sharedContent as any).state,
      sharedContent.finishedByTime,
      sharedContent.ticketCounts
    );
    this.dateTime = dateTime;
    this.numBookable = numBookable;
    this.state = 'TIME_FLEXIBLE';
  }

  getDescriptionString() {
    return `${this.ticketGroup.maxPurchaseSize}`;
  }

  getSquareColor() {
    return 'SquareColor--flex';
  }

  getStatus() {
    return '';
  }

  getActions(context: Context, options?: GetActionsOptions): CalendarItemAction[] {
    const isReservationOpen = !!(options && options.isReservationOpen);
    const actions: CalendarItemAction[] = [
      {
        key: CalendarItemActionNames.MODIFY,
        disabled: false,
        isConfirm: false,
        text: 'Modify',
        calendarItemState: this.state,
      },
      {
        disabled: !this.isBookable || isReservationOpen,
        key: CalendarItemActionNames.SELL,
        isConfirm: false,
        showInModify: true,
        text: 'Book reservation',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Book now',
          },
        },
        calendarItemState: this.state,
      },
      {
        disabled: !this.isBookable || (options && options.hideBookMultiple) || isReservationOpen,
        key: CalendarItemActionNames.BOOK_ON_MULTIPLE,
        isConfirm: false,
        showInModify: true,
        text: 'Book on multiple tables',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Book on multiple',
          },
        },
        calendarItemState: this.state,
      },
      {
        key: CalendarItemActionNames.SEND_TO_WAITLIST,
        disabled: false,
        isConfirm: false,
        text: 'Alert Notify list',
        amplitude: {
          event: EventAction.CALENDAR_ITEM_QUICK_ACTION,
          data: {
            calendarItemQuickAction: 'Alert Notify list',
            actionLocation: EventAttributesActionLocation.TIMELINE_BODY,
          },
        },
        showInModify: true,
        calendarItemState: this.state,
      },
    ];

    return actions;
  }
}

/**
 * Ticket groups price can be edited if
 *    && no tickets on the communal table are locked,
 * @param {Object} ticketGroup the ticket group being considered
 * @returns {boolean} true if any of these peers are locked
 */
const peerTicketGroupIsLocked = (ticketGroup: core.TicketGroup) => {
  // todo:  figure out a way to check if any ticket groups with same type and dateTime are locked
  // those should not be price updateable either, but there isn't a clean way to get them
  return (
    ticketGroup &&
    ticketGroupHelpers.isCommunal(ticketGroup) &&
    some(ticketGroup.ticketSubset, (ticketSubset) => {
      return ticketSubset.state === 'LOCKED';
    })
  );
};

/**
 * Returns true if the ticket types associated with a ticket group contain
 * one that is bookable.
 * @param {Object[]} ticketTypes the ticket types associated with a ticket group
 * @returns {boolean} true if there is a bookable ticket type.
 */
const isBookable = (ticketTypes: any) => {
  return some(ticketTypes, 'isBookable');
};

const calendarItems = {
  /**
   * Constructs a new calendarItem with the given properties.
   *
   * @returns {Object} Returns a calendar item builder for constructing the different types of calendar items
   */
  createCalendarItem(
    id: string,
    ticketCounts?: TicketCounts,
    ticketGroup?: core.TicketGroup,
    isCommunalGroupFollower?: boolean
  ) {
    // We cast ticketGroup to type `any` because, with walk-ins, it's a lie
    // that every calendarItem has a ticketGroup. However, changing this type
    // will force us to go update a ton of places... (YK 2018/10/29)
    const sharedContent: SharedContent = {
      id,
      finishedByTime: ticketGroup && ticketGroup.finishedByTime,
      ticketGroup: ticketGroup as any,
      peerTicketGroupIsLocked: !!(ticketGroup && peerTicketGroupIsLocked(ticketGroup)),
      isBookable: !!(ticketGroup && isBookable(ticketGroup.ticketType)),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      shortCode: isCommunalGroupFollower ? undefined : oc(ticketGroup).ticketType[0].shortCode()!,
      ticketCounts,
    };
    return {
      /**
       * Constructs a record describing an available calendar item
       *
       * @returns {Object} A newly created available calendar item
       */
      available(
        allowedPurchaseSizes: number[],
        numBookable: number,
        maxSize: number,
        annotationText: string
      ): CalendarItem {
        return new Available(
          sharedContent,
          sharedContent.isBookable ? numBookable : 0,
          allowedPurchaseSizes,
          maxSize,
          annotationText
        );
      },
      /**
       * Constructs a record describing a hold for a guest on the calendar
       *
       * @returns {Object} A newly created held calendar item
       */
      held(
        allowedPurchaseSizes: number[],
        numBookable: number,
        maxSize: number,
        ticketSubset: core.TicketSubset
      ): CalendarItem {
        return new Held(
          sharedContent,
          sharedContent.isBookable ? numBookable : 0,
          allowedPurchaseSizes,
          maxSize,
          ticketSubset
        );
      },
      /**
       * Constructs a record describing a locked calendar item
       *
       */
      locked(ticketSubset: core.TicketSubset, purchaseVerifyInfo?: any): CalendarItem {
        return new Locked(sharedContent, purchaseVerifyInfo, ticketSubset);
      },
      /**
       * Constructs a record describing a sold calendar item
       *
       * @returns {Object} A newly created sold calendar item
       */
      sold(purchase: any, forfeited?: any): Sold {
        return new Sold(sharedContent, purchase, forfeited);
      },
      /**
       * Constructs a record describing a walk-in calendar item
       *
       * @param party the service object representing a walk-in party
       * @param dateTime
       *
       * @returns A newly created walk-in calendar item
       */
      walkin(party: Service.Party, dateTime: string): WalkIn {
        return new WalkIn(sharedContent, party, dateTime);
      },
      /**
       * Constructs a record describing a communal table calendar item
       *
       * @returns {Object} A newly created communal table calendar item
       */
      communal(forfeited: number): CalendarItem {
        return new Communal(sharedContent, forfeited);
      },
      timeFlexible(dateTime: string, numBookable: number): CalendarItem {
        return new TimeFlexible(
          sharedContent,
          dateTime,
          sharedContent.isBookable ? numBookable : 0
        );
      },
    };
  },
  /**
   * Returns all calendar items associated with the given ticket group.
   *
   * @param {TicketGroup} ticketGroup the ticket group to compute calendar items for
   * @returns {CalendarItem[]} all calendar items associated with this ticket group
   */
  computeCalendarItemsForTicketGroup(ticketGroup: core.TicketGroup): CalendarItem[] {
    const minPurchaseSize: number = +ticketGroup.minPurchaseSize;
    const maxPurchaseSize: number = +ticketGroup.maxPurchaseSize;

    if (ticketGroup.tableVariety === TableTypeConstants.COMMUNAL_TABLE) {
      let counter = 0;
      const calendarItems: CalendarItem[] = [];
      const ticketCounts = calculateTicketCounts(ticketGroup);
      const { forfeited, numAvailable, numHeldTickets } = ticketCounts;

      calendarItems.push(
        this.createCalendarItem(
          ticketGroup.id + '-' + counter++,
          ticketCounts,
          ticketGroup
        ).communal(forfeited)
      );
      if (numAvailable > 0) {
        calendarItems.push(
          this.createCalendarItem(
            ticketGroup.id + '-' + counter++,
            ticketCounts,
            ticketGroup,
            true
          ).available(
            range(
              Math.min(minPurchaseSize, numAvailable),
              Math.min(maxPurchaseSize, numAvailable) + 1
            ),
            numAvailable + numHeldTickets,
            ticketGroup.maxSize,
            `${numAvailable}`
          )
        );
      }
      counter = this.addTicketSubsetsToCalendarItems(
        ticketGroup,
        ticketCounts,
        calendarItems,
        counter
      );
      //tslint:disable-next-line:no-dead-store
      counter = this.addPurchasesToCalendarItems(ticketGroup, ticketCounts, calendarItems, counter);
      return calendarItems;
    } else if (ticketGroup.finishedByTime) {
      const bookableIntervals = getBookableIntervalsForFlexibleTicketGroup(ticketGroup);
      // Start at the size of the bookable intervals, for wonky re-selection reasons
      let counter = size(bookableIntervals);
      const calendarItems: CalendarItem[] = [];
      const ticketCounts = calculateTicketCounts(ticketGroup);
      const subsetResult = this.addTicketSubsetsToCalendarItemsAndReturnItems(
        ticketGroup,
        ticketCounts,
        calendarItems,
        counter
      );
      counter = subsetResult.counter;
      const purchaseResult = this.addPurchasesToCalendarItemsAndReturnItems(
        ticketGroup,
        ticketCounts,
        calendarItems,
        counter
      );
      //tslint:disable-next-line:no-dead-store
      counter = purchaseResult.counter;
      const calendarItemsForTimes = map(bookableIntervals, (interval) => {
        return this.createCalendarItem(
          ticketGroup.id + `-` + counter++,
          ticketCounts,
          ticketGroup
        ).timeFlexible(interval.toString(), ticketGroup.maxSize);
      });
      const allCalendarItmes = concat(calendarItemsForTimes, calendarItems);
      if (allCalendarItmes.length === 0) {
        throw new Error('Unexpected ticket group state: ' + oc(ticketGroup).state());
      }
      return allCalendarItmes;
    } else if (ticketGroup.state === TicketGroupStateConstants.HELD) {
      const ticketSubset = ticketGroup.ticketSubset[0] as core.TicketSubset;
      const subsetSize = ticketSubset.ticketCount;
      const ticketCounts = calculateTicketCounts(ticketGroup);
      return [
        this.createCalendarItem(ticketGroup.id + '-0', ticketCounts, ticketGroup).held(
          range(minPurchaseSize, maxPurchaseSize + 1),
          ticketGroup.maxSize,
          subsetSize,
          ticketSubset
        ),
      ];
    } else if (ticketGroup.state === TicketGroupStateConstants.LOCKED) {
      const ticketSubset = find(
        ticketGroup.ticketSubset,
        (subset) => subset.state === 'LOCKED'
      ) as core.TicketSubset;
      const ticketCounts = calculateTicketCounts(ticketGroup);
      return [
        this.createCalendarItem(ticketGroup.id + '-0', ticketCounts, ticketGroup).locked(
          ticketSubset,
          ticketSubset.purchaseVerifyInfo
        ),
      ];
    } else if (ticketGroup.state === TicketGroupStateConstants.AVAILABLE) {
      const ticketCounts = calculateTicketCounts(ticketGroup);
      return [
        this.createCalendarItem(ticketGroup.id + '-0', ticketCounts, ticketGroup).available(
          range(minPurchaseSize, maxPurchaseSize + 1),
          ticketGroup.maxSize,
          ticketGroup.maxSize,
          ''
        ),
      ];
    } else if (ticketGroup.state === TicketGroupStateConstants.SOLD) {
      const ticketCounts = calculateTicketCounts(ticketGroup);
      if (ticketGroup.purchaseDetail.length === 0) {
        throw new Error('Unexpected ticket group state:');
      }
      return map(ticketGroup.purchaseDetail as core.PastPurchase[], (purchase, index) => {
        return this.createCalendarItem(
          ticketGroup.id + '-' + index,
          ticketCounts,
          ticketGroup
        ).sold(purchase, maxPurchaseSize - purchase.ticketCount);
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('Unexpected ticket group state: ' + oc(ticketGroup).state());
      return [];
    }
  },
  addPurchasesToCalendarItemsAndReturnItems(
    ticketGroup: core.TicketGroup,
    ticketCounts: TicketCounts,
    calendarItems: CalendarItem[],
    initialCounter: number
  ) {
    let counter = initialCounter;
    const purchases = sortBy(ticketGroup.purchaseDetail, [
      'ticketCount',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (p) => (!p.dinerPatron ? -1 : p.dinerPatron![0].lastName),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (p) => (!p.dinerPatron ? -1 : p.dinerPatron![0].firstName),
    ]);
    const newItems = map(purchases, (purchase: core.PastPurchase) => {
      // TODO [Blake] add new calendar item type here for walkin purchase?
      const item = this.createCalendarItem(
        ticketGroup.id + '-' + counter++,
        ticketCounts,
        ticketGroup,
        true
      ).sold(purchase, 0);
      calendarItems.push(item);
      return item;
    });
    return {
      counter,
      newItems,
    };
  },
  addPurchasesToCalendarItems(
    ticketGroup: core.TicketGroup,
    ticketCounts: TicketCounts,
    calendarItems: CalendarItem[],
    initialCounter: number
  ) {
    return this.addPurchasesToCalendarItemsAndReturnItems(
      ticketGroup,
      ticketCounts,
      calendarItems,
      initialCounter
    ).counter;
  },
  addTicketSubsetsToCalendarItemsAndReturnItems(
    ticketGroup: core.TicketGroup,
    ticketCounts: TicketCounts,
    calendarItems: CalendarItem[],
    initialCounter: number
  ) {
    let counter = initialCounter;
    const ticketSubsets = sortBy(ticketGroup.ticketSubset, 'ticketCount') as core.TicketSubset[];
    const { numAvailable, numHeldTickets } = ticketCounts;
    const newItems: CalendarItem[] = [];
    ticketSubsets.forEach((ticketSubset: core.TicketSubset) => {
      if (ticketSubset.state === 'HELD') {
        const subsetSize: number = +ticketSubset.ticketCount;
        const newItem = this.createCalendarItem(
          ticketGroup.id + '-' + counter++,
          ticketCounts,
          ticketGroup,
          true
        ).held(
          range(subsetSize, subsetSize + 1),
          numAvailable + numHeldTickets,
          subsetSize,
          ticketSubset
        );
        newItems.push(newItem);
        calendarItems.push(newItem);
      } else if (ticketSubset.state === 'LOCKED') {
        const newItem = this.createCalendarItem(
          ticketGroup.id + '-' + counter++,
          ticketCounts,
          ticketGroup,
          true
        ).locked(ticketSubset, ticketSubset.purchaseVerifyInfo);
        newItems.push(newItem);
        calendarItems.push(newItem);
      } else {
        assertNever(ticketSubset.state);
      }
    });
    return {
      counter,
      newItems,
    };
  },
  addTicketSubsetsToCalendarItems(
    ticketGroup: core.TicketGroup,
    ticketCounts: TicketCounts,
    calendarItems: CalendarItem[],
    initialCounter: number
  ) {
    return this.addTicketSubsetsToCalendarItemsAndReturnItems(
      ticketGroup,
      ticketCounts,
      calendarItems,
      initialCounter
    ).counter;
  },
  /**
   * Returns all calendar items associated with the given walk-in party.
   *
   * @param party the party to compute calendar items for
   * @param dateTime the
   * @returns the calendar item associated with this party
   */
  computeCalendarItemsForWalkInParty(party: Service.Party, dateTime: string): WalkIn {
    return this.createCalendarItem(oc(party).id() + '-0').walkin(party, dateTime);
  },
  /**
   * Returns all calendar items associated with the given booked party.
   *
   * @param party the party to compute calendar items for
   * @returns the calendar item associated with this party
   */
  computeCalendarItemsForBookedParty(party: Service.Party): Sold {
    return this.createCalendarItem(oc(party).id() + '-0').sold(party);
  },
};

export default calendarItems;

interface TicketCounts {
  forfeited: number;
  numAvailable: number;
  numHeldTickets: number;
  numLockedTickets: number;
  numPurchasedTickets: number;
}

export interface CalendarItemMeta {
  tableId?: number;
  isBlocked?: boolean;
}

export function calculateTicketCounts(ticketGroup: core.TicketGroup): TicketCounts {
  let numPurchasedTickets = 0;
  forEach(ticketGroup.purchaseDetail as core.PastPurchase[], (purchaseDetail) => {
    numPurchasedTickets += purchaseDetail.ticketCount;
  });
  let numSubsetTickets = 0;
  forEach(ticketGroup.ticketSubset as core.TicketSubset[], (ticketSubset) => {
    numSubsetTickets += ticketSubset.ticketCount;
  });
  const numAvailable = ticketGroup.maxSize - numPurchasedTickets - numSubsetTickets;
  let numHeldTickets = 0;
  const heldSubsets = filter(ticketGroup.ticketSubset, (ticketSubset) => {
    return ticketSubset.state === TicketGroupStateConstants.HELD;
  });
  forEach(heldSubsets as core.TicketSubset[], (ticketSubset) => {
    numHeldTickets += oc(ticketSubset).ticketCount() || 0;
  });
  const numLockedTickets = numSubsetTickets - numHeldTickets;

  let forfeited;
  if (numAvailable < ticketGroup.minPurchaseSize) {
    forfeited = numAvailable;
  } else if (ticketGroup.isEvensOnly && numAvailable % 2 !== 0) {
    forfeited = 1;
  } else {
    forfeited = 0;
  }
  return { forfeited, numAvailable, numHeldTickets, numLockedTickets, numPurchasedTickets };
}

export function getCalendarItemForTicketGroupSubset(
  ticketGroupsByDateTime: TicketGroupsByDateTime,
  ticketGroupId: number,
  ticketGroupSubsetId: number
): CalendarItem | undefined {
  let calendarItem: CalendarItem | undefined;

  forEach(ticketGroupsByDateTime, (ticketGroups: ElaboratedTicketGroup[]) => {
    forEach(ticketGroups, (ticketGroup: ElaboratedTicketGroup) => {
      if (ticketGroup.id === ticketGroupId) {
        calendarItem = find(ticketGroup.calendarItems, (calendarItem: CalendarItem) => {
          return oc(calendarItem).ticketSubset.id() === ticketGroupSubsetId;
        });
        return false;
      }
      return undefined;
    });
    if (calendarItem) {
      return false;
    }
    return undefined;
  });

  return calendarItem;
}

/**
 * Search a set of ticket groups to find a calendar item with a provided purchase
 * @param ticketGroupsByDateTime Ticket Groups to search
 * @param purchaseId purchase id to search by
 * @param useOriginalPurchaseId specify to search by original purchase id instead of current purchase id
 */
export function getCalendarItemForPurchase(
  ticketGroupsByDateTime: TicketGroupsByDateTime,
  purchaseId: number,
  options = {
    useOriginalPurchaseId: false,
  }
): CalendarItem | undefined {
  let calendarItem: CalendarItem | undefined;
  // return false breaks es-toolkit forEach loop
  // with return false, typescript complains without return undefined for other paths
  forEach(ticketGroupsByDateTime, (ticketGroups) => {
    forEach(ticketGroups, (ticketGroup) => {
      forEach(ticketGroup.calendarItems, (calendarItemDetail) => {
        if (!calendarItemDetail.purchase) {
          return undefined;
        }
        if (options.useOriginalPurchaseId) {
          if (purchaseId === calendarItemDetail.purchase.originalPurchaseId) {
            calendarItem = calendarItemDetail;
            return false;
          }
        } else if (purchaseId === calendarItemDetail.purchase.id) {
          calendarItem = calendarItemDetail;
          return false;
        }
        return undefined;
      });
      if (calendarItem) {
        return false;
      }
      return undefined;
    });
    if (calendarItem) {
      return false;
    }
    return undefined;
  });
  return calendarItem;
}

/**
 * Search a set of ticket groups to find a calendar item with a provided purchase
 * @param ticketGroupsByDateTime Ticket Groups to search
 * @param purchaseId purchase id to search by
 * @param useOriginalPurchaseId specify to search by original purchase id instead of current purchase id
 */
export function getCalendarItemForParty(
  party: Service.Party,
  ticketGroupsByDateTime: TicketGroupsByDateTime,
  options = {
    useOriginalPurchaseId: false,
  }
): CalendarItem | undefined {
  let calendarItem: CalendarItem | undefined;

  if (!party.purchase) {
    return undefined;
  }

  // return false breaks es-toolkit forEach loop
  // with return false, typescript complains without return undefined for other paths
  forEach(ticketGroupsByDateTime, (ticketGroups) => {
    forEach(ticketGroups, (ticketGroup) => {
      forEach(ticketGroup.calendarItems, (calendarItemDetail) => {
        if (!oc(calendarItemDetail).purchase()) {
          return undefined;
        }
        if (options.useOriginalPurchaseId) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          if (party.purchase!.id === oc(calendarItemDetail).purchase.originalPurchaseId()) {
            calendarItem = calendarItemDetail;
            return false;
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        } else if (party.purchase!.id === oc(calendarItemDetail).purchase.id()) {
          calendarItem = calendarItemDetail;
          return false;
        }
        return undefined;
      });
      if (calendarItem) {
        return false;
      }
      return undefined;
    });
    if (calendarItem) {
      return false;
    }
    return undefined;
  });
  return calendarItem;
}

export const getTableAssignmentFromCalendarItem = (
  calendarItem: CalendarItem
): number[] | undefined => {
  if (calendarItem.state !== 'SOLD') {
    return;
  }

  return calendarItem instanceof WalkIn
    ? calendarItem.party.tableIds
    : calendarItem.purchase.tableId;
};
